// src/hooks/education-completion-hooks.ts
//
// Mindtickle education-completion tree for the Leader "Enablement" tab.
//
// `GET snowflake/education/completion/me/` walks the requesting user's org straight from the toolbox
// `user` table and returns, for every person at/under them, the FLAT LIST of Mindtickle series they were
// assigned (`own.items`) plus a completion `summary`. There is no program bucketing: Mindtickle's own
// INVITE_TYPE / MODULE_RELEVANCE columns are unreliable for this org, so which series COUNT toward a
// learner's required-completion metric is admin-curated (see the education/scope/* endpoints). Each item
// therefore carries `in_scope` / `required` / `window` resolved from that curation, and every headline
// figure (`required_*`, `avg_percent_completed`, `required_completion_rate`) is computed over the
// REQUIRED set only. Self-enrolled series are surfaced but never counted against a learner.
//
// Two fetch shapes are exported. `useEducationCompletion` pulls the WHOLE subtree once (the `/me/` tree,
// re-rootable by an allowlisted `global.proxy` leader via `root=`). `useEducationCompletionByEmail` is the
// scalable per-node drill (`/by-email/`): it fetches ONE node at a time — own detail, subtree rollup, and
// direct-report summary stubs — so drilling refetches for a report's email instead of holding the entire
// org forest in memory. The Leader dashboard uses the by-email drill.
//
// Both endpoints 400 in local/demo mode (`USE_LOCAL_DATA`) and 403 out of org scope, so callers must
// degrade gracefully: `retry: false` keeps a 400 from thrashing, and the Enablement pages fall back to
// journey-map-only tiles plus an "education unavailable locally" note when the node is null.

import { useQuery } from '@tanstack/react-query'; //-tech-debt-react-query;
import { apiGet } from '../support/aiApi';
import { DEFAULT_QUERY_OPTIONS } from '../support/constants';

/** One assigned Mindtickle series for a person, with its admin-curated scope resolved. */
export interface EducationItem {
  series_id: string;
  series_name: string;
  status: 'completed' | 'in_progress' | 'not_started';
  /** Percent of the series' modules completed (0–100). */
  percent_completed: number;
  total_modules: number;
  completed_modules: number;
  /** REQUIRED-module subcounts; completion + percent are measured over these, not all modules. */
  total_required_modules?: number;
  completed_required_modules?: number;
  /** Raw Mindtickle SeriesState (e.g. ACTIVE / ARCHIVE), or null. */
  series_state?: string | null;
  /** Series is closed/archived in Mindtickle — no longer actionable, never surfaced as required. */
  closed?: boolean;
  certified: boolean;
  /** Certified AND the certificate's award date falls in the reporting window. */
  certified_in_period: boolean;
  /** Raw Mindtickle INVITE_TYPE (mostly UNKNOWN for this org; only SELF is trusted). */
  invite_type: string;
  /** INVITE_TYPE === 'SELF' — surfaced but never counted against the learner. */
  self_enrolled: boolean;
  certificate_name: string | null;
  /** ISO date (YYYY-MM-DD) of the certificate award, or null. */
  award_date: string | null;
  /** Latest due date (ISO date) among the series' REQUIRED modules, or null when none has one. */
  due_date?: string | null;
  /** Where `due_date` came from: the learner's own DueDate, or the module's due-date rule. */
  due_date_source?: 'learner' | 'rule' | null;
  /** ISO date the series was assigned to this person. */
  assigned_on?: string | null;
  /** ISO date the last required module was finished; only set for a completed series. */
  completed_on?: string | null;
  /** ISO date the certificate lapses. */
  expires_on?: string | null;
  /** Admin-curated: this series is in scope for the reporting window. */
  in_scope: boolean;
  /** ADMIN-invited: assigned enablement — the ONLY set counted toward the "X of X" required metric. */
  required: boolean;
  /** The curated cadence window (current_quarter | current_fiscal_year | always_on), or null. */
  window: string | null;
  /** Optional admin label grouping the series (e.g. "QEP"), or null. */
  program_label: string | null;
}

/** Completion counts for one person (`own`) or their whole subtree (`rollup`). Required-centric. */
export interface EducationSummary {
  series_total: number;
  completed_series: number;
  /** Assigned series currently in progress (all-scope). Used for the required_total==0 fallback. */
  in_progress_series: number;
  /** Assigned series not yet started (all-scope). Used for the required_total==0 fallback. */
  not_started_series: number;
  required_total: number;
  required_completed: number;
  required_in_progress: number;
  required_not_started: number;
  self_enrolled_total: number;
  self_enrolled_completed: number;
  /** Assigned series that are neither required nor self-enrolled (not curated into scope). */
  informational_total: number;
  certified_total: number;
  certified_in_period: number;
  /** Mean percent-complete across REQUIRED series (0–100); null until scope is curated. */
  avg_percent_completed: number | null;
  /** required_completed / required_total (0–1); null until scope is curated. */
  required_completion_rate: number | null;
}

/** One node of the education roll-up tree (mirrors `education_tree.build_tree`). */
export interface EducationNode {
  email: string;
  cecid: string | null;
  is_manager: boolean;
  /** This person's own assigned series (`items`) plus their completion `summary`. */
  own: { items: EducationItem[]; summary: EducationSummary };
  /** This person + every descendant, rolled up (counts only; items live on each `own`). */
  rollup: { summary: EducationSummary };
  reports: EducationNode[];
}

export interface EducationCompletionResponse {
  success: boolean;
  source?: string;
  query_name?: string;
  email?: string;
  is_manager?: boolean;
  time_filter?: string;
  window?: { start?: string; end?: string } | null;
  scope_size?: number;
  person_count?: number;
  tree: EducationNode | null;
}

export interface UseEducationCompletionOptions {
  /** Snowflake time window; defaults server-side to `current_fiscal_year`. */
  timeFilter?: string;
  /**
   * Re-root the report at another user's org. Only honored for an allowlisted caller (global.proxy →
   * kturner); a normal leader leaves this undefined and reports on their own org.
   */
  rootEmail?: string;
  /** react-query overrides (e.g. `enabled`). */
  queryOptions?: object;
}

/**
 * The education-completion tree for the current user (or `rootEmail`, when allowlisted). Fetched once;
 * all drill re-rooting is client-side. Errors (e.g. the local-mode 400) surface as `isError` with no
 * data so the Enablement pages can show journey-map tiles alone.
 */
export function useEducationCompletion(opts: UseEducationCompletionOptions = {}) {
  const { timeFilter, rootEmail, queryOptions } = opts;
  return useQuery<EducationCompletionResponse>({
    queryKey: ['education-completion', timeFilter ?? 'current_fiscal_year', rootEmail ?? 'self'],
    queryFn: () =>
      apiGet<EducationCompletionResponse>('snowflake/education/completion/me/', {
        time_filter: timeFilter,
        root: rootEmail,
      }),
    ...{ ...DEFAULT_QUERY_OPTIONS, retry: false, ...(queryOptions || {}) },
  });
}

// ---------------------------------------------------------------------------
// By-email drill (the scalable per-node counterpart to the whole-tree /me/ fetch)
// ---------------------------------------------------------------------------
//
// `GET snowflake/education/completion/by-email/?email=` returns a SHALLOW envelope for a single org
// node: this person's `own` (items + summary) and whole-subtree `rollup`, plus each DIRECT report as a
// stub carrying only its own + rollup *summaries* (no items, no deeper reports). The frontend drills by
// calling this endpoint again for a report's email, so each payload stays proportional to one node's
// span-of-control instead of the whole org forest. Org-scoped server-side: a caller may address any
// email at/under themselves, or any email if they hold the global-proxy allowlist.

/** A direct report of a drill node — summaries only (drill again by email for its items/reports). */
export interface EducationDrillReport {
  email: string;
  cecid: string | null;
  is_manager: boolean;
  own: { summary: EducationSummary };
  rollup: { summary: EducationSummary };
}

/** One org node addressed by email: own detail + subtree rollup + direct-report stubs. */
export interface EducationDrillNode {
  email: string;
  cecid: string | null;
  is_manager: boolean;
  own: { items: EducationItem[]; summary: EducationSummary };
  rollup: { summary: EducationSummary };
  reports: EducationDrillReport[];
}

export interface EducationDrillResponse {
  success: boolean;
  source?: string;
  query_name?: string;
  email?: string;
  is_manager?: boolean;
  time_filter?: string;
  window?: { start?: string; end?: string } | null;
  scope_size?: number;
  node: EducationDrillNode | null;
}

/**
 * The education drill node for a single `email` (the person the dashboard is currently focused on).
 * `enabled` only when an email is supplied; errors (e.g. the local-mode 400, or an out-of-scope 403)
 * surface as `isError` with no data so the Enablement pages degrade to journey-map-only tiles.
 */
export function useEducationCompletionByEmail(
  email?: string,
  opts: { timeFilter?: string; queryOptions?: object } = {},
) {
  const { timeFilter, queryOptions } = opts;
  const target = String(email || '').trim().toLowerCase();
  return useQuery<EducationDrillResponse>({
    queryKey: ['education-completion-by-email', target, timeFilter ?? 'current_fiscal_year'],
    queryFn: () =>
      apiGet<EducationDrillResponse>('snowflake/education/completion/by-email/', {
        email: target,
        time_filter: timeFilter,
      }),
    ...{ ...DEFAULT_QUERY_OPTIONS, retry: false, enabled: !!target, ...(queryOptions || {}) },
  });
}

// ---------------------------------------------------------------------------
// Required series by team (the manager view's per-series breakdown)
// ---------------------------------------------------------------------------
//
// `GET snowflake/education/completion/required-series/?email=` lists every REQUIRED series assigned to
// anyone at/under `email`. Per series: the node's own status, status counts for everyone under them, and each
// DIRECT report's own status. A direct report who manages people also carries `team`: status counts for
// everyone under them, not including them (their own status is `own`). `team_size` is how many people those
// counts are drawn from, so "assigned" can be set against it ("not assigned" = team_size - assigned).

export interface RequiredSeriesStatus {
  status: 'completed' | 'in_progress' | 'not_started';
  percent_completed: number;
  completed_modules: number;
  total_modules: number;
  total_required_modules?: number | null;
  completed_required_modules?: number | null;
  certified: boolean;
  certificate_name: string | null;
}

export interface StatusCounts {
  assigned: number;
  completed: number;
  in_progress: number;
  not_started: number;
}

export interface RequiredSeriesReport {
  email: string;
  cecid: string | null;
  is_manager: boolean;
  /** This person's own status; null when the series is not assigned to them. */
  own: RequiredSeriesStatus | null;
  /** Counts for everyone under them (managers only). */
  team: StatusCounts | null;
  team_size: number;
}

export interface RequiredSeriesEntry {
  series_id: string;
  series_name: string;
  own: RequiredSeriesStatus | null;
  team: StatusCounts;
  reports: RequiredSeriesReport[];
}

export interface RequiredSeriesNode {
  email: string;
  cecid: string | null;
  is_manager: boolean;
  team_size: number;
  series: RequiredSeriesEntry[];
}

export interface RequiredSeriesResponse {
  success: boolean;
  time_filter?: string;
  window?: { start?: string; end?: string } | null;
  scope_size?: number;
  node: RequiredSeriesNode | null;
}

/** Required series by team for `email`'s org. Errors (local-mode 400, out-of-scope 403) surface as `isError`. */
export function useEducationRequiredSeries(
  email?: string,
  opts: { timeFilter?: string; queryOptions?: object } = {},
) {
  const { timeFilter, queryOptions } = opts;
  const target = String(email || '').trim().toLowerCase();
  return useQuery<RequiredSeriesResponse>({
    queryKey: ['education-required-series', target, timeFilter ?? 'current_fiscal_year'],
    queryFn: () =>
      apiGet<RequiredSeriesResponse>('snowflake/education/completion/required-series/', {
        email: target,
        time_filter: timeFilter,
      }),
    ...{ ...DEFAULT_QUERY_OPTIONS, retry: false, enabled: !!target, ...(queryOptions || {}) },
  });
}
